import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CreditCard, ExternalLink, CheckCircle2, AlertTriangle, Loader2, RefreshCw } from "lucide-react";

/**
 * StripeConnectPanel — Dashboard settings block for real-money payouts.
 *
 * State machine:
 *   NOT-CONNECTED   → "Set up payouts" button → POST /stripe/connect/onboard
 *                     → redirect to Stripe hosted onboarding
 *   PENDING         → account exists but charges/payouts NOT enabled →
 *                     "Continue setup" (reuses the same onboard endpoint)
 *   READY           → "Manage on Stripe" button + status pill
 *
 * When the streamer returns from Stripe (`?connect=return` in URL), we
 * force-refresh the status via POST /stripe/connect/refresh-status.
 */
export default function StripeConnectPanel() {
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/stripe/connect/status");
      setStatus(r.data);
    } catch { setStatus({ connected: false }); }
  };

  useEffect(() => { load(); }, []);

  // Force-refresh when Stripe kicks the user back
  useEffect(() => {
    if (params.get("connect") === "return") {
      (async () => {
        try {
          const r = await api.post("/stripe/connect/refresh-status");
          setStatus(prev => ({ ...(prev || {}), ...r.data, connected: true, platform_fee_pct: prev?.platform_fee_pct }));
          if (r.data.onboarded) toast.success("Payouts are now live!");
        } catch { /* status refresh best-effort */ }
        // Strip the query param so a reload doesn't re-fire it
        const next = new URLSearchParams(params);
        next.delete("connect");
        setParams(next, { replace: true });
      })();
    }
  }, [params, setParams]);

  const startOnboarding = async () => {
    setBusy(true);
    try {
      const r = await api.post("/stripe/connect/onboard", { origin_url: window.location.origin });
      if (r.data?.onboarding_url) {
        window.location.href = r.data.onboarding_url;
      } else {
        toast.error("Stripe returned no onboarding URL");
        setBusy(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Onboarding failed");
      setBusy(false);
    }
  };

  const openStripeDashboard = async () => {
    setBusy(true);
    try {
      const r = await api.get("/stripe/connect/login-link");
      if (r.data?.url) window.open(r.data.url, "_blank", "noopener");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open Stripe dashboard");
    } finally { setBusy(false); }
  };

  const forceRefresh = async () => {
    setBusy(true);
    try {
      const r = await api.post("/stripe/connect/refresh-status");
      setStatus(prev => ({ ...(prev || {}), ...r.data, connected: true, platform_fee_pct: prev?.platform_fee_pct }));
    } catch (e) { toast.error(e?.response?.data?.detail || "Refresh failed"); }
    finally { setBusy(false); }
  };

  if (!status) {
    return (
      <div className="sc-card p-5" data-testid="stripe-connect-panel">
        <div className="sc-label">Payouts</div>
        <div className="text-xs text-zinc-500 sc-mono mt-2">Loading…</div>
      </div>
    );
  }

  const feePct = ((status.platform_fee_pct ?? 0.10) * 100).toFixed(0);
  const streamerPct = 100 - Number(feePct);

  return (
    <div className="sc-card p-5" data-testid="stripe-connect-panel">
      <div className="flex items-center gap-3 mb-1">
        <CreditCard className="w-4 h-4 text-[color:var(--sc-cyan)]" />
        <div className="sc-label" style={{ color: "var(--sc-cyan)" }}>Payouts (real money)</div>
      </div>
      <h3 className="sc-heading text-xl font-bold mb-1">Stripe Connect</h3>
      <p className="text-xs text-zinc-500 sc-mono mb-5">
        Real USD subs land in your Stripe balance. You keep <span className="text-[color:var(--sc-volt)] font-bold">{streamerPct}%</span> — MystiCast keeps <span className="text-[color:var(--sc-pink)] font-bold">{feePct}%</span>. Stripe pays you on their default schedule.
      </p>

      {!status.connected && (
        <>
          <div className="text-sm text-zinc-300 mb-3">
            You haven&apos;t set up payouts yet. Complete onboarding on Stripe (takes ~2 minutes) to start receiving real-money channel subs.
          </div>
          <button
            onClick={startOnboarding}
            disabled={busy}
            data-testid="stripe-connect-onboard"
            className="sc-btn text-sm inline-flex items-center gap-2"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
            {busy ? "Redirecting…" : "Set up payouts on Stripe"}
          </button>
        </>
      )}

      {status.connected && !status.onboarded && (
        <>
          <div className="p-3 rounded-sm border border-[color:var(--sc-pink)]/30 bg-[color:var(--sc-pink)]/5 flex items-start gap-3 mb-4" data-testid="stripe-connect-pending">
            <AlertTriangle className="w-4 h-4 text-[color:var(--sc-pink)] mt-0.5 shrink-0" />
            <div className="text-xs">
              <div className="font-bold text-[color:var(--sc-pink)]">Setup incomplete</div>
              <div className="text-zinc-400 sc-mono mt-1">
                Charges enabled: {status.charges_enabled ? "✓" : "✗"} · Payouts enabled: {status.payouts_enabled ? "✓" : "✗"}
                <br />
                Finish the last steps on Stripe to start receiving payments.
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={startOnboarding}
              disabled={busy}
              data-testid="stripe-connect-continue"
              className="sc-btn text-sm inline-flex items-center gap-2"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
              Continue setup
            </button>
            <button
              onClick={forceRefresh}
              disabled={busy}
              data-testid="stripe-connect-refresh"
              className="sc-btn-ghost text-sm inline-flex items-center gap-2"
            ><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
          </div>
        </>
      )}

      {status.onboarded && (
        <>
          <div className="p-3 rounded-sm border border-[color:var(--sc-volt)]/40 bg-[color:var(--sc-volt)]/10 flex items-start gap-3 mb-4" data-testid="stripe-connect-ready">
            <CheckCircle2 className="w-4 h-4 text-[color:var(--sc-volt)] mt-0.5 shrink-0" />
            <div className="text-xs">
              <div className="font-bold text-[color:var(--sc-volt)]">Payouts are LIVE</div>
              <div className="text-zinc-400 sc-mono mt-1">Every real-money sub is auto-split. Your share lands in your Stripe balance and pays out on Stripe&apos;s schedule.</div>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={openStripeDashboard}
              disabled={busy}
              data-testid="stripe-connect-manage"
              className="sc-btn text-sm inline-flex items-center gap-2"
            ><ExternalLink className="w-4 h-4" /> Manage on Stripe</button>
            <button
              onClick={forceRefresh}
              disabled={busy}
              className="sc-btn-ghost text-sm inline-flex items-center gap-2"
              data-testid="stripe-connect-refresh"
            ><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
          </div>
        </>
      )}
    </div>
  );
}
